import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Lock, Users, Zap } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1601929862217-f1bf94503333?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjYXIlMjBoaWdod2F5JTIwZHJpdmluZyUyMGx1eHVyeSUyMGR1c2t8ZW58MHx8fHwxNzg5ODYyMDcwfDA&ixlib=rb-4.1.0&q=85";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.15 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.6 + i * 0.12, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden grain">
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY, scale: bgScale }}>
        <img src={HERO_IMG} alt="Veículo em rodovia ao anoitecer" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B3E7F]/90 via-[#083463]/85 to-[#062B52]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 w-full">
        <motion.p
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="font-mono-brand text-xs tracking-[0.25em] uppercase text-[#7CC4F0] mb-6"
        >
          Despachantes de veículos · Todo o Brasil
        </motion.p>

        <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-white max-w-4xl">
          <span className="hero-line-mask">
            <motion.span custom={0} initial="hidden" animate="visible" variants={lineReveal} className="block">
              Precisa resolver uma questão
            </motion.span>
          </span>
          <span className="hero-line-mask">
            <motion.span custom={1} initial="hidden" animate="visible" variants={lineReveal} className="block">
              relacionada ao <span className="text-[#7CC4F0]">seu veículo?</span>
            </motion.span>
          </span>
        </h1>

        <motion.h2
          custom={1}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="font-heading text-base md:text-lg text-slate-200 mt-6 max-w-2xl"
        >
          Encontre o profissional mais indicado para a sua necessidade.
        </motion.h2>

        <motion.p
          custom={2}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="text-sm md:text-base text-slate-300 mt-4 max-w-2xl leading-relaxed"
        >
          Informe seu estado, cidade e o serviço que você precisa. Nós direcionaremos sua
          solicitação ao profissional adequado para atender sua demanda.
        </motion.p>

        <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp} className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <a
            href="#solicitar"
            data-testid="hero-cta-button"
            className="btn-cta inline-flex items-center gap-3 bg-[#2E9FDB] text-[#06305F] font-heading font-bold text-sm sm:text-base px-8 py-4 rounded-full uppercase tracking-wide"
          >
            Solicitar atendimento
            <ArrowDown className="w-4 h-4" />
          </a>
          <div className="flex items-center gap-5 text-slate-300 text-xs font-mono-brand">
            <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#7CC4F0]" /> Dados protegidos</span>
            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#7CC4F0]" /> Profissionais qualificados</span>
            <span className="hidden sm:flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-[#7CC4F0]" /> Resposta ágil</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
